import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import parse from 'html-react-parser';

import { useIntl } from 'react-intl';

import { CHANGE_PASSWORD_RESET_CREDENTIAL_FORM_FIELDS, RESET_CREDENTIAL } from 'settings/constants/login';
import { TAC_STEP_FINAL, TAC_STEP_INITIAL } from 'settings/constants/tac';
import {
  ERR_INVALID_ACCOUNT_AND_OTP,
  ERR_INVALID_ACCOUNT_NUMBER,
  ERR_INVALID_CODE,
  ERR_INVALID_OTP_NUMBER,
  ERR_INVALID_TAC,
  ERR_INVALID_USER_AND_CODE,
  ERR_INVALID_USER_ID,
  ERR_MAXIMUM_TAC,
  ERR_SAME_OLD_PASSWORD,
  MESSAGE_SUCCESS,
  RESPONSE_CODE_SUCCESS_TAC,
} from 'settings/constants/reset-credential';
import { DRAWER_TYPE_ERROR, MODAL_FULLSCREEN_SIZE, SIDEBAR_VARIANT } from 'settings/constants/ui-control';

import { FormModal } from 'components/common/modals';
import { isEmpty } from 'lodash';
import { encrypt, generateSHA256Hash } from 'utils/crypto';
import {
  getTacResetCredential,
  getUpdatePasswordResetCredential,
  getVerifyCaptchaResetCredential,
  getVerifyTacResetCredential,
  putTacResetCredential,
  putUpdatePasswordResetCredential,
} from 'middleware/actions/login';

import { TacDrawer } from 'components/common/drawers';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { formatDynamicFields } from 'utils/dynamic-field';
import { getChangePasswordResetCredentialDynamicField, getPasswordValidatorMapper } from 'utils/settings/change-password/getter';
import { isPasswordMatchResetCredential } from 'utils/validation';

import SecondResetForm from 'components/reset-credential/SecondResetForm';
import RenewPasswordForm from 'components/reset-credential/RenewPasswordForm';
import PasswordChecklist from 'components/settings/security/PasswordChecklist';
import ResetForm from 'components/reset-credential/ResetForm';
import ResetCredentialFooter from 'components/reset-credential/Footer';

const ResetCredential = ({ locale, state, isOpen, onClose, captcha, setDrawer, onChange, onRefresh }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(MODAL_FULLSCREEN_SIZE));

  const [disableButton, setDisableButton] = useState(false);
  const [firstForm, setFirstForm] = useState(true);
  const [secondForm, setSecondForm] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState(false);
  const [isPasswordMatch, setPasswordMatch] = useState(false);

  const {
    userId,
    codeCaptcha,
    captchaId,
    accountNumber,
    otpNumber,
    tacStepResetCredential,
    isTacDrawerOpenResetCredential,
    verifyCaptchaData,
    verifySecondData,
    getDataTac,
    updatePasswordCredential,
    isDrawerOpen,
    newPasswordResetCredential,
    confirmNewPasswordResetCredential,
    validationCaptcha,
    validationTrxId,
    validationTacTrxId,
  } = state;
  const { responseMessage, cif } = verifyCaptchaData;
  const { TEMPORARY } = SIDEBAR_VARIANT;

  const responseMessageVerifyOtp = isEmpty(verifySecondData) ? '' : verifySecondData.responseMessage;
  const responseHashAccountTrxId = isEmpty(verifySecondData) ? '' : verifySecondData.hashAccount;
  const responseCodeGetTac = isEmpty(getDataTac) ? '' : getDataTac.responseCode;
  const responseUpdatePassword = isEmpty(updatePasswordCredential) ? '' : updatePasswordCredential.responseMessage;

  // concat account no + cif + trx id
  const hasConcatAccountTrxId = generateSHA256Hash(`${accountNumber}${cif}${validationTrxId}`);
  const hasConcatUserId = generateSHA256Hash(`${isEmpty(getDataTac) ? '' : getDataTac.userId}${validationTacTrxId}`);

  const invalidUser = responseMessage === ERR_INVALID_USER_ID;
  const invalidCaptcha = responseMessage === ERR_INVALID_CODE;
  const invalidUserAndCaptcha = responseMessage === ERR_INVALID_USER_AND_CODE;
  const invalidAccountNumber = responseMessageVerifyOtp === ERR_INVALID_ACCOUNT_NUMBER;
  const invalidOtpNumber = responseMessageVerifyOtp === ERR_INVALID_OTP_NUMBER;
  const invalidAccountAndOtp = responseMessageVerifyOtp === ERR_INVALID_ACCOUNT_AND_OTP;
  const invalidMaxTac2 = responseMessageVerifyOtp === ERR_MAXIMUM_TAC;
  const invalidTac = responseUpdatePassword === ERR_INVALID_TAC;
  const invalidMaxTac = responseUpdatePassword === ERR_MAXIMUM_TAC;
  const invalidUpdatePassword = responseUpdatePassword === ERR_SAME_OLD_PASSWORD;
  const isSuccess = responseMessage === MESSAGE_SUCCESS;
  const isSuccessVerifyOtp = responseMessageVerifyOtp === MESSAGE_SUCCESS;
  const isSuccessGetTac = responseCodeGetTac === RESPONSE_CODE_SUCCESS_TAC;
  const isSuccessUpdatePassword = responseUpdatePassword === MESSAGE_SUCCESS;
  const validHasAccountTrxId = hasConcatAccountTrxId === responseHashAccountTrxId;

  const checklistValidators = getPasswordValidatorMapper(newPasswordResetCredential);
  const formFields = formatDynamicFields(
    CHANGE_PASSWORD_RESET_CREDENTIAL_FORM_FIELDS,
    getChangePasswordResetCredentialDynamicField(state, checklistValidators)
  );

  const notes = (
    <PasswordChecklist
      newPassword={newPasswordResetCredential}
      validators={checklistValidators}
      title={formatMessage({ id: 'settings.security.changePassword.newPasswordCriteria.title' })}
    />
  );

  const closeModal = () => {
    onChange({ ...state, userId: '', codeCaptcha: '' });
    onClose(false);
    setFirstForm(true);
    setSecondForm(false);
  };

  const closeModalPasswordForm = () => {
    onChange({
      ...state,
      userId: '',
      codeCaptcha: '',
      cifNumber: '',
      accountNumber: '',
      otpNumber: '',
      newPasswordResetCredential: '',
      confirmNewPasswordResetCredential: '',
      verifySecondData: {},
    });
    setChangePasswordForm(false);
    setFirstForm(true);
    setSecondForm(false);
  };

  const handleChange = (name, value) => onChange({ [name]: value });

  const handleSubmit = () => dispatch(getVerifyCaptchaResetCredential({ userId, captcha: codeCaptcha, captchaId }));
  const handleSubmitSecondForm = () =>
    dispatch(getVerifyTacResetCredential({ accountNo: accountNumber, otp: otpNumber, cif, captchaId, hashAccount: hasConcatAccountTrxId }));

  const handleSubmitPasswordForm = () => {
    onChange({ ...state, isTacDrawerOpenResetCredential: true });
    setChangePasswordForm(false);
  };

  const handleBack = () => {
    onChange({ ...state, userId: '', codeCaptcha: '', cifNumber: '', accountNumber: '', otpNumber: '' });
    setFirstForm(true);
    setSecondForm(false);
    onRefresh();
  };

  const handleRequestTac = () => {
    // clear out response before calling TAC request again
    dispatch(putTacResetCredential());

    // If passed validation, call TAC request
    dispatch(getTacResetCredential({ userId: verifySecondData.userId, captchaId, locale }));
  };

  const handleSubmitTac = tacValue => {
    // clear tac response code before resubmit tac again
    dispatch(putUpdatePasswordResetCredential());
    dispatch(
      getUpdatePasswordResetCredential({
        userId: getDataTac.userId,
        tac: tacValue,
        hashedPassword: encrypt(newPasswordResetCredential),
        hashUserId: hasConcatUserId,
      })
    );
  };

  const handleTacClose = () => {
    onChange({
      ...state,
      isTacDrawerOpenResetCredential: false,
      userId: '',
      codeCaptcha: '',
      cifNumber: '',
      accountNumber: '',
      otpNumber: '',
      newPasswordResetCredential: '',
      confirmNewPasswordResetCredential: '',
      validationTacTrxId: '',
    });

    setFirstForm(true);
    setSecondForm(false);
    setChangePasswordForm(false);
  };

  useEffect(() => {
    // handle button footer
    if (!secondForm) {
      if (isEmpty(userId) || isEmpty(codeCaptcha)) setDisableButton(true);
      if (!isEmpty(userId) && !isEmpty(codeCaptcha)) setDisableButton(false);
    }
  }, [userId, codeCaptcha, secondForm]);

  useEffect(() => {
    // handle button footer
    if (secondForm) {
      onChange({
        ...state,
        validationCaptcha: '',
      });

      if (isEmpty(accountNumber) || isEmpty(otpNumber)) setDisableButton(true);
      if (!isEmpty(accountNumber) && !isEmpty(otpNumber)) setDisableButton(false);
    }
  }, [accountNumber, otpNumber, secondForm, disableButton]);

  useEffect(() => {
    // handle error tac drawer
    if (!isEmpty(verifyCaptchaData) && !isEmpty(responseMessage)) {
      if (invalidUser) setDrawer({ id: 'resetCredential.errorMessage.invalidUserId' }, DRAWER_TYPE_ERROR);
      if (invalidCaptcha || validationCaptcha !== codeCaptcha) {
        setDrawer({ id: 'resetCredential.errorMessage.invalidCode' }, DRAWER_TYPE_ERROR);

        if (validationCaptcha !== codeCaptcha && isSuccess) {
          setFirstForm(true);
          setSecondForm(false);
          setDrawer({ id: 'resetCredential.errorMessage.invalidCode' }, DRAWER_TYPE_ERROR);
          return;
        }
      }
      if (invalidUserAndCaptcha) setDrawer({ id: 'resetCredential.errorMessage.invalidUserAndCode' }, DRAWER_TYPE_ERROR);

      if (isSuccess) {
        setFirstForm(false);
        setSecondForm(true);
        onChange({ ...state, validationTrxId: verifyCaptchaData.trxId });
      }
    }
  }, [verifyCaptchaData]);

  useEffect(() => {
    if (!isEmpty(verifySecondData) && !isEmpty(responseMessageVerifyOtp)) {
      if (!validHasAccountTrxId) {
        setDrawer({ id: 'resetCredential.errorMessage.invalidAccountOtpNumber' }, DRAWER_TYPE_ERROR);
        return;
      }

      if (invalidAccountNumber) setDrawer({ id: 'resetCredential.errorMessage.invalidAccountOtpNumber' }, DRAWER_TYPE_ERROR);
      if (invalidOtpNumber) setDrawer({ id: 'resetCredential.errorMessage.invalidAccountOtpNumber' }, DRAWER_TYPE_ERROR);
      if (invalidAccountAndOtp) setDrawer({ id: 'resetCredential.errorMessage.invalidAccountOtpNumber' }, DRAWER_TYPE_ERROR);
      if (invalidMaxTac2) setDrawer({ id: 'resetCredential.errorMessage.maxTac' }, DRAWER_TYPE_ERROR);

      if (isSuccessVerifyOtp) {
        setSecondForm(false);
        setChangePasswordForm(true);
        onChange({ ...state, isResetCredentialOpen: false, validationTrxId: '' });
      }
    }
  }, [verifySecondData]);

  useEffect(() => {
    if ((invalidUser || invalidCaptcha || invalidUserAndCaptcha) && isDrawerOpen) onRefresh();
  }, [isDrawerOpen]);

  useEffect(() => {
    setPasswordMatch(isPasswordMatchResetCredential(newPasswordResetCredential, confirmNewPasswordResetCredential, checklistValidators));
  }, [newPasswordResetCredential, confirmNewPasswordResetCredential, checklistValidators]);

  useEffect(() => {
    if (isSuccessGetTac) onChange({ ...state, tacStepResetCredential: TAC_STEP_FINAL });
  }, [responseCodeGetTac]);

  useEffect(() => {
    if (!isEmpty(updatePasswordCredential) && !isEmpty(responseUpdatePassword)) {
      if (invalidUser) setDrawer({ id: 'resetCredential.errorMessage.invalidUserId' }, DRAWER_TYPE_ERROR);
      if (invalidTac) setDrawer({ id: 'resetCredential.errorMessage.invalidTac' }, DRAWER_TYPE_ERROR);
      if (invalidMaxTac) setDrawer({ id: 'resetCredential.errorMessage.maxTac' }, DRAWER_TYPE_ERROR);
      if (invalidUpdatePassword) setDrawer({ id: 'resetCredential.errorMessage.invalidOldPassword' }, DRAWER_TYPE_ERROR);
      if (isSuccessUpdatePassword) {
        onChange({
          ...state,
          userId: '',
          codeCaptcha: '',
          cifNumber: '',
          accountNumber: '',
          otpNumber: '',
          newPasswordResetCredential: '',
          confirmNewPasswordResetCredential: '',
          validationCaptcha: '',
          validationTrxId: '',
          validationTacTrxId: '',
          tacStepResetCredential: TAC_STEP_INITIAL,
          verifySecondData: {},
          verifyCaptchaData: {},
          getDataTac: {},
          isTacDrawerOpenResetCredential: false,
        });
        setFirstForm(true);
        setSecondForm(false);
        setChangePasswordForm(false);
        setDrawer({ id: `resetCredential.tac.descriptionFinal` }, '', true);
      }
    }
  }, [updatePasswordCredential]);

  return (
    <>
      <FormModal
        fullWidth
        isOpen={isOpen}
        title={formatMessage({ id: 'resetCredential.modal.title' })}
        onClose={closeModal}
        contentTitle={formatMessage({ id: 'resetCredential.modal.header.title' })}
        description={formatMessage({ id: 'resetCredential.modal.header.description' })}
        typeModal={RESET_CREDENTIAL}
        footer={
          <ResetCredentialFooter
            empty={disableButton}
            handleSubmit={!secondForm ? handleSubmit : handleSubmitSecondForm}
            handleBack={handleBack}
            isDisabled={secondForm}
          />
        }
      >
        <form noValidate className="ph4 pv3 reset-credential">
          {firstForm && !changePasswordForm && (
            <ResetForm state={state} captcha={captcha} onChange={onChange} onRefresh={onRefresh} handleChange={handleChange} />
          )}
          {secondForm && !changePasswordForm && <SecondResetForm state={state} onChange={onChange} handleChange={handleChange} />}
        </form>
      </FormModal>

      {changePasswordForm && (
        <RenewPasswordForm
          fullScreen={fullScreen}
          onSubmit={handleSubmitPasswordForm}
          isOpen={changePasswordForm}
          onClose={closeModalPasswordForm}
          title="login.renewPasswordAlert.title"
          formDetail={state}
          onChange={onChange}
          fields={formFields}
          hasNoteContent
          notes={notes}
          isRenewPassword
          isPasswordMatch={isPasswordMatch}
          actionButtonTitle="buttonText.continue"
          resetCredential
        />
      )}

      {isTacDrawerOpenResetCredential && (
        <TacDrawer
          variant={TEMPORARY}
          currentStep={tacStepResetCredential}
          hideBackdrop={false}
          timestamp={`${moment(new Date()).tz('Asia/Jakarta').format('Do MMMM YYYY HH:mm:ss')} GMT +7`}
          isOpen={isTacDrawerOpenResetCredential}
          onRequestTac={handleRequestTac}
          onClose={handleTacClose}
          onSubmitTac={tacValue => handleSubmitTac(tacValue)}
          title={formatMessage({ id: 'resetCredential.tac.title' })}
          description={parse(formatMessage({ id: 'resetCredential.tac.description' }))}
        />
      )}
    </>
  );
};

ResetCredential.defaultProps = {
  locale: '',
  state: {},
};

ResetCredential.propTypes = {
  locale: PropTypes.string,
  state: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  captcha: PropTypes.string.isRequired,
  setDrawer: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default ResetCredential;
